import {
  IoAlertCircleSharp,
  IoCheckmarkCircleSharp,
  IoInformationCircleSharp,
} from 'react-icons/io5';
import { MText } from './';
import { MBox, MFlex, MIcon } from './chakra';

import { ToastItem } from '@monetize/types/app';
import { MdClose } from 'react-icons/md';

// Toast bg/icon colors are semantic and intentionally per-severity. The
// foreground/accent color is the same `fg.primary` everywhere (the legacy
// `tGary.acBlack` was a typo for `tGray.acBlack` which silently fell back —
// `fg.primary` is the V2 semantic equivalent).
/**
 * Pins every toast to the `maxW` width, so a short message ("Network Error")
 * doesn't shrink to its content beside a wrapping one. v2's toast container
 * enforced a min width; v3's doesn't. From `md` up only — the toaster spans
 * the viewport with side insets below that.
 */
const TOAST_MIN_WIDTH = { md: 96 };

const getValues = (severity: string | undefined): any => {
  switch (severity) {
    case 'success':
      return {
        bgColor: 'tGreen.light',
        hoverColor: 'tGreen.light',
        accentColor: 'fg.primary',
        icon: IoCheckmarkCircleSharp,
        iconColor: 'tGreen.mDarkShade',
      };
    case 'error':
      return {
        bgColor: 'tRed.cosmos',
        hoverColor: 'tRed.cosmos',
        accentColor: 'fg.primary',
        icon: IoAlertCircleSharp,
        iconColor: 'tRed.base',
      };
    case 'warning':
      return {
        bgColor: 'tCreamsicle.base',
        hoverColor: 'tCreamsicle.base',
        accentColor: 'fg.primary',
        icon: IoAlertCircleSharp,
        iconColor: 'tOrange.tangerine',
      };

    case 'info':
    default:
      return {
        bgColor: 'tPurple.linkWater',
        hoverColor: 'tPurple.linkWater',
        accentColor: 'fg.primary',
        icon: IoInformationCircleSharp,
        iconColor: 'tIndigo.base',
      };
  }
};
const MToast = ({
  id,
  severity,
  summary,
  summaryProps,
  detail,
  detailsProps,
  onClose,
  customContent,
}: {
  id?: string;
  onClose: (id?: string) => void;
} & ToastItem): JSX.Element => {
  const { bgColor, hoverColor, accentColor, icon, iconColor } =
    getValues(severity);

  return (
    <MBox
      minW={TOAST_MIN_WIDTH}
      maxW={96}
      p={4}
      bgColor={bgColor}
      borderRadius="md"
      boxShadow="md"
      transition="all ease 0.3s"
      _hover={{
        bgColor: hoverColor,
      }}
    >
      <MFlex justify="space-between" gap={4}>
        <MFlex align="center">
          <MIcon as={icon} w={6} h={6} color={iconColor} />
        </MFlex>
        <MBox w="full" overflow="auto">
          <MText
            lineClamp={1}
            fontSize="sm"
            fontWeight="bold"
            color={accentColor}
            {...summaryProps}
          >
            {summary || ''}
          </MText>
          {detail && (
            <MText fontSize="xs" color={accentColor} {...detailsProps}>
              {detail}
            </MText>
          )}
          {customContent}
        </MBox>
        <MFlex
          align="flex-start"
          justify="center"
          onClick={() => onClose(id)}
          mt={1}
        >
          <MIcon
            as={MdClose}
            w={4}
            h={4}
            color={accentColor}
            cursor="pointer"
          />
        </MFlex>
      </MFlex>
    </MBox>
  );
};

export default MToast;
